import React, {useCallback, useMemo, useState} from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Breakpoint,
  useTheme,
  useMediaQuery,
  DialogTitle
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import {useTranslation} from 'react-i18next';
import ConfirmationDialog from './confirmation-dialog';
import {MatrixEvent} from 'matrix-js-sdk';
import {MultimediaDownloadInfo} from '@/types/matrix';

// TODO: DO we wan't to give whole event as property in chat mode?
interface DialogWithCloseIconProps {
  open: boolean;
  onClose: () => void;
  hide?: boolean;
  fullWidth?: boolean;
  maxWidth?: false | Breakpoint;
  right?: number;
  top?: number;
  chatMode?: boolean;
  multimediaInfo?: MultimediaDownloadInfo;
  children?: React.ReactNode;
  matrixEvent?: MatrixEvent;
  onDeleteEvent?: (
    parentImageId: MatrixEvent,
    imageToDeleteUrl: string
  ) => void;
  onDeleted?: () => void;
  onDownload?: (multimediaInfo: MultimediaDownloadInfo) => void;
  isOwnMessage?: boolean;
  customDialogTitle?: string;
  customDialogContent?: string;
  isChatWindow?: boolean;
  chatUserName?: string | null;
  maxHeight?: string;
}

const DialogWithCloseIcon: React.FC<DialogWithCloseIconProps> = ({
  open,
  onClose,
  hide,
  fullWidth = true,
  maxWidth = 'lg',
  right = 18,
  top = -2,
  chatMode,
  children,
  matrixEvent,
  onDeleteEvent,
  onDeleted,
  onDownload,
  isOwnMessage,
  customDialogTitle = 'delete_message_title',
  customDialogContent = 'delete_message_dialog_content',
  multimediaInfo,
  isChatWindow,
  chatUserName,
  maxHeight
}) => {
  const {t} = useTranslation();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const handleOpenDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpen(true);
  }, []);

  const handleCloseDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpen(false);
  }, []);

  const handleDeleteClick = useCallback(async () => {
    try {
      if (onDeleteEvent && matrixEvent && multimediaInfo) {
        onDeleteEvent(matrixEvent, multimediaInfo.url);
      }
      if (onDeleted) {
        onDeleted();
      }

      handleCloseDeleteConfirmation();
    } catch (error) {
      console.error('Failed to delete the message:', error);
    }
  }, [
    handleCloseDeleteConfirmation,
    multimediaInfo,
    matrixEvent,
    onDeleteEvent,
    onDeleted
  ]);

  const sizeStyles = useMemo(() => {
    let chatSizeStyles = {};
    if (isChatWindow) {
      if (isSmallScreen) {
        chatSizeStyles = {width: '280px', maxWidth: '280px'};
      } else if (isMediumScreen) {
        chatSizeStyles = {width: '360px', maxWidth: '360px'};
      } else if (isLargeScreen) {
        chatSizeStyles = {width: '740px', maxWidth: '740px'};
      } else {
        chatSizeStyles = {width: '580px', maxWidth: '580px'};
      }
    }
    return chatSizeStyles;
  }, [isChatWindow, isSmallScreen, isMediumScreen, isLargeScreen]);

  return (
    <>
      <Dialog
        open={open}
        hidden={hide}
        onClose={onClose}
        maxWidth={isChatWindow ? false : maxWidth}
        fullWidth={fullWidth}
        fullScreen={chatMode && fullScreen}
        PaperProps={{style: {position: 'relative', ...sizeStyles}}}
        sx={{
          maxHeight: maxHeight ? maxHeight : 'auto'
        }}
      >
        {isChatWindow && chatUserName && (
          <DialogTitle
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            {t('direct_chat_with', {userName: chatUserName})}
          </DialogTitle>
        )}

        {chatMode && onDeleteEvent && isOwnMessage && (
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => {
              handleOpenDeleteConfirmation();
            }}
            aria-label={t('delete')}
            sx={{
              position: 'absolute',
              right: right + 98,
              top: chatMode ? 0 : top
            }}
          >
            <DeleteIcon />
          </IconButton>
        )}
        {chatMode && onDownload && multimediaInfo && (
          <IconButton
            edge="end"
            color="inherit"
            onClick={() =>
              onDownload({
                url: multimediaInfo.url,
                type: multimediaInfo.type,
                mimeType: multimediaInfo.mimeType
              })
            }
            aria-label={t('download')}
            sx={{
              position: 'absolute',
              right: right + 48,
              top: chatMode ? 2 : top
            }}
          >
            <DownloadIcon />
          </IconButton>
        )}

        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label={t('close')}
          sx={{
            position: 'absolute',
            right,
            top: chatMode ? 2 : top
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent
          id="scrollableDiv"
          sx={{
            margin: chatMode ? 4 : 0
          }}
        >
          {children}
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={deleteConfirmationOpen}
        title={t(customDialogTitle)}
        message={t(customDialogContent)}
        onConfirm={handleDeleteClick}
        onCancel={handleCloseDeleteConfirmation}
      />
    </>
  );
};

export default DialogWithCloseIcon;
